import {
  Payment,
  PaymentIntentResponse,
  CheckoutResponse,
  CustomerPortalRedirectResponse,
  DefaultPaymentMethodResponse,
  DonationIntentResponse,
  AdyenClientSession,
  VippsCheckoutResponse,
  VippsStatusResponse,
  LastCancelledSubscriptionTypeResponse,
} from '~/types/payment'
import { homApi } from '~/redux/apis/hom'
import { FAMILY_API_TAGS } from '~/redux/constants/tags.const'

import { getMetaCookies } from '~/utils/cookie.utils'

import { Coupon } from '~/subscription/types/subscription.types'

import { invalidateOnSuccess } from '../utils/util'

import { PageRequest } from './types'

export const paymentApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    createIntent: builder.mutation<PaymentIntentResponse, { orderId: number }>({
      query: (payload) => ({
        url: `/v1/payments/stripe/intent`,
        method: 'POST',
        body: payload,
      }),
    }),

    redirectCustomerPortal: builder.mutation<CustomerPortalRedirectResponse, { redirectUrl: string }>({
      query: (payload) => ({
        url: `/v1/payments/stripe/subscriptions/redirect-customer-portal`,
        method: 'POST',
        body: payload,
      }),
    }),

    subscriptionApplicabileInGeoLocation: builder.query<boolean, void>({
      query: () => ({
        url: `/v1/payments/geo-loc-subscription-applicable`,
        method: 'GET',
      }),
    }),

    subscriptionApplicability: builder.query<boolean, void>({
      query: () => ({
        url: `/v1/payments/subscription-applicable`,
        method: 'GET',
      }),
    }),

    getDiscountCoupon: builder.query<{ couponCode: string }, void>({
      query: () => ({
        url: `/v1/payments/subscription-discount-coupon`,
        method: 'GET',
      }),
    }),

    checkout: builder.query<
      CheckoutResponse | AdyenClientSession,
      {
        priceId: string
        currency: string
        couponCode?: string
        discardTrial?: boolean
        promotionId?: string
        referrer?: string
        fbp?: string
        fbc?: string
        isFamilySubscription?: boolean
        licenseCount?: number
        hasAdminLicense?: boolean
        reactiveMemberIds?: number[]
        /** The reactivation screen made a choice — even if that choice was nobody */
        hasReactivationSelection?: boolean
      }
    >({
      query: (payload) => ({
        url: `/v1/payments/stripe/checkout`,
        method: 'GET',
        params: { ...payload, ...getMetaCookies() },
      }),
    }),
    upgradeToFamilyAccount: builder.mutation<
      {
        adyenSession?: AdyenClientSession
        success: boolean
        message: string
        requiresPayment: boolean
        vippsSession?: VippsCheckoutResponse
      },
      {
        licenseCount: number
        hasAdminLicense: boolean
        familyPriceId: string
        // Validated against familyPriceId, so a mismatched code is refused.
        couponCode?: string
        promotionId?: string
      }
    >({
      query: (payload) => ({
        url: `/v1/family/upgrade-to-family`,
        method: 'POST',
        body: { ...payload },
      }),
      invalidatesTags: invalidateOnSuccess([FAMILY_API_TAGS.Family]),
    }),
    vippsCheckout: builder.mutation<
      VippsCheckoutResponse,
      {
        priceId: string
        currency: string
        couponCode?: string
        promotionId?: string
        referrer?: string
        redirectUrl?: string
        locale: string
        isFamilySubscription?: boolean
        licenseCount?: number
        hasAdminLicense?: boolean
        discardTrial?: boolean
      }
    >({
      query: (payload) => ({
        url: `/v1/payments/vipps/checkout`,
        method: 'POST',
        body: payload,
      }),
    }),

    vippsAgreementStatus: builder.query<VippsStatusResponse, { reference: string; isEPayment?: boolean }>({
      query: (payload) => ({
        url: `/v1/payments/vipps/agreement-status`,
        method: 'GET',
        params: payload,
      }),
    }),

    updatePayment: builder.mutation<
      { success: true },
      { fullNameOrCompanyName: string; email: string; cardholderName: string; paymentIntentId: string }
    >({
      query: (payload) => ({
        url: `/v1/payments/stripe/update-payment`,
        method: 'PATCH',
        body: payload,
      }),
    }),

    updatePaymentUpgradeDowngradeSubscription: builder.mutation<
      any,
      // `licenseCount` is required for family and cannot go below the current count — only the admin
      // panel can pick who loses access. A coupon is validated against the target price.
      {
        subscriptionId: string
        updateToPriceId: string
        licenseCount?: number
        couponCode?: string
        promotionId?: string
      }
    >({
      query: (payload) => ({
        url: `/v1/payments/stripe/update-payment-subscription`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: invalidateOnSuccess([FAMILY_API_TAGS.Family]),
    }),

    getReceiptsList: builder.query<{ results: Payment[]; total: number }, PageRequest>({
      query: (params) => ({
        url: '/v1/payments',
        method: 'GET',
        params,
      }),
    }),

    getDefaultPaymentMethod: builder.query<DefaultPaymentMethodResponse, void>({
      query: () => ({
        url: '/v1/payments/stripe/default-payment-method',
        method: 'GET',
      }),
    }),

    getLastCancelledSubscriptionType: builder.query<LastCancelledSubscriptionTypeResponse, void>({
      query: () => ({
        url: '/v1/payments/stripe/last-cancelled-subscription-type',
        method: 'GET',
      }),
    }),

    getDonationIntent: builder.query<DonationIntentResponse, { amount: number; currency: string }>({
      query: (params) => ({
        url: '/v1/payments/stripe/donation-intent',
        method: 'GET',
        params,
      }),
    }),

    getPromotion: builder.query<{ promotionId: string; coupon: Coupon }, { promoCode: string }>({
      query: (params) => ({
        url: '/v1/payments/stripe/promotion',
        method: 'GET',
        params,
      }),
    }),
  }),
})

export const {
  useCreateIntentMutation,
  useRedirectCustomerPortalMutation,
  useGetReceiptsListQuery,
  useCheckoutQuery,
  useUpdatePaymentUpgradeDowngradeSubscriptionMutation,
  useSubscriptionApplicabilityQuery,
  useGetDiscountCouponQuery,
  useGetDefaultPaymentMethodQuery,
  useGetLastCancelledSubscriptionTypeQuery,
  useLazyGetLastCancelledSubscriptionTypeQuery,
  useGetDonationIntentQuery,
  useUpdatePaymentMutation,
  useGetPromotionQuery,
  useLazyGetPromotionQuery,
  useLazySubscriptionApplicabileInGeoLocationQuery,
  useVippsCheckoutMutation,
  useVippsAgreementStatusQuery,
  useUpgradeToFamilyAccountMutation,
  util: { getRunningQueriesThunk },
} = paymentApi

export const { subscriptionApplicability, getPromotion } = paymentApi.endpoints
export const { useQueryState: useSubscriptionApplicabilityQueryState } = subscriptionApplicability
